import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RiPlaneLine, RiTeamLine, RiCheckboxCircleLine, RiTimeLine, RiFileTextLine, RiArticleLine, RiCalendarLine, RiFlightTakeoffLine, RiFlightLandLine, RiArrowRightLine, RiBuilding2Line, RiFileAddLine, RiAlertLine } from '@remixicon/react';
import { Card, CardHeader, CardContent, CardFooter } from '@components/ui/Card';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { getQuoteStatusConfig } from '../utils';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS, ROLES, normalizeRole } from '@/lib/permissions';
/**
 * QuoteCard Component
 * Displays a single quote with client info, flight details, and actions
 *
 * @param {Object} props
 * @param {Object} props.quote - Quote data object
 * @param {Function} props.onViewQuote - Callback when "View Quote" is clicked
 */
const formatPaymentStatus = (status) => {
  if (!status) return '—';
  return status
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const QuoteCard = ({ quote, onViewQuote, hideActions = false }) => {
  const navigate = useNavigate();

  // "Build Trip Sheet" requires create + update on Booking & Quoting
  // (admin, broker, junior_broker — read-only roles excluded).
  // flight_coordinator is included because trip sheets are operations work
  // they own via the Tracking module.
  const { can, role } = usePermissions();
  const canBuildTripSheet =
    (can(MODULES.BOOKING, ACTIONS.CREATE) && can(MODULES.BOOKING, ACTIONS.UPDATE)) ||
    normalizeRole(role) === ROLES.FLIGHT_COORDINATOR;

  const handleViewClick = () => {
    if (onViewQuote) {
      onViewQuote(quote);
    } else {
      navigate(`/quotes/${quote.id}`);
    }
  };


  // Resolve data from either bid_items (quotes endpoint) or bids (client endpoint)
  const firstBid = quote.bid_items?.[0]?.bid || quote.bids?.[0] || {};
  const legs = firstBid.leg_itinerary || [];
  const bidOptions = firstBid.bid_options || quote.bid_items?.[0]?.bid_options || [];

  return (
    <Card className="group hover:shadow-lg transition-all duration-200 overflow-hidden bg-white !rounded-3xl">
      {/* Client Info Section */}
      <CardHeader className="p-5 pb-4 !border-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative shrink-0">
              {quote.client?.isVerified && (
                <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                  <RiCheckboxCircleLine className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-md truncate">
                {quote.client?.first_name} {quote.client?.last_name}
              </h3>
              <p className="text-xs text-gray-700 truncate flex items-center gap-1">
                <RiBuilding2Line className="w-3 h-3 text-gray-700" />
                <span>{quote.client?.company_name || quote.client?.company || '-'}</span>
              </p>
            </div>
          </div>

          {(() => {
            const config = getQuoteStatusConfig(quote.status);
            const Icon = config.icon;
            const statusUpper = (quote.status || '').toUpperCase();
            const needsPaidAction =
              (statusUpper === 'SENT' || statusUpper === 'VIEWED' || statusUpper === 'SIGNED') &&
              (quote.payment_status || '').toUpperCase() === 'CAPTURED';
            return (
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md ${config.className}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />}
                  {config.label}
                </span>
                {needsPaidAction && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-200 whitespace-nowrap"
                    title="Payment captured but quote is not yet marked as Paid"
                  >
                    <RiAlertLine className="w-3 h-3" />
                    Mark as Paid
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      </CardHeader>

      <CardContent className="px-5 pt-0 pb-4 space-y-2">
        {/* Flight Routes */}
        <div className="space-y-2 border-t border-gray-100 pt-2">
          {/* Outbound Flight */}
          {legs.map((leg, i) => (
            <div key={`ml-${i}`} className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {leg.departure_airport}</span>
                <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{leg.arrival_airport}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <RiCalendarLine className="w-3.5 h-3.5" />
                  <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "MMM D")}</span>
                </div>
                <div className="flex items-center gap-1">
                  <RiTimeLine className="w-3.5 h-3.5" />
                  <span>{formatUTCToDateTime(leg.departure_date, getAirportTimezone(leg.departure_airport), "HH:mm")}</span>
                </div>
              </div>
            </div>
          ))}

        </div>

        {/* Additional Info */}
        <div className="text-xs text-gray-500 pt-2 pb-3 space-y-2">
          <div className="flex items-center gap-1.5">
            <RiTeamLine className="w-3.5 h-3.5" />
            <span>{quote.passenger_count || 0} passenger{quote.passenger_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <RiPlaneLine className="w-3.5 h-3.5" />
            <span>{bidOptions.length} tail options</span>
          </div>
        </div>

        {/* Payment Status — shown only for accepted quotes */}
        {quote.status === 'ACCEPTED' && (
          <div className="flex items-center bg-gray-100 justify-between border-t border-gray-100 px-3 py-2 text-sm rounded-lg">
            <span className="text-gray-500">Payment Status</span>
            <span className="font-medium text-gray-900">
              {formatPaymentStatus(quote.payment_status)}
            </span>
          </div>
        )}

        {/* View Quote Button */}
        {!hideActions && (
        <div className="flex gap-2">
          <button
            onClick={handleViewClick}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent text-primary-500 text-sm rounded-full transition-colors mt-3"
          >
            <RiArticleLine className="w-4 h-4" />
            <span>View Quote</span>
          </button>

          {canBuildTripSheet && (quote.status=="ACCEPTED" || quote.status=="PASSENGERS_PENDING" || quote.status=="WAITING_FLIGHT") &&
          <button
            onClick={()=> navigate(`/trip-sheet?quoteid=${quote.id}`)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 text-white text-sm rounded-full transition-colors mt-3"
          >
            <RiFileAddLine className="w-4 h-4" />
            <span>Build Trip Sheet</span>
          </button>
          }
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuoteCard;